import React, { useEffect, useState } from 'react';
import { Input, Table, Button, DOMHelper, Stack, IconButton, Divider, Tag } from 'rsuite';
import type { SortType } from 'rsuite';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import EditIcon from '@rsuite/icons/Edit';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import TablePagination from '@/components/TablePagination';
import { useNavigate } from 'react-router-dom';
import FetchingSelect from '@/components/FetchingSelect';

import { ClubEventStatusList, ClubEventVisibilityList, RunningClubEventList, RunningClubEventPaginationFilterRequest } from '@/models/running-club-event.model';
import { getPagedRunningClubEvents } from '@/services/running-club-event.service';
import { getRunningClubsLookup } from '@/services/running-club.service';
import { jsDateToZone } from '@/utils/date-time';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

interface RunningClubEventListProps {
  runningClubId?: number;
}
const RunningClubEventListPage : React.FC<RunningClubEventListProps> = ({runningClubId}) => {
  const [filterReq, setFilterReq] = useState<RunningClubEventPaginationFilterRequest>(new RunningClubEventPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunningClubEventList[]>([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: RunningClubEventPaginationFilterRequest) => {
    setLoading(true);
    try {
      const finalReq = {
        ...req,
        runningClubId: runningClubId ?? req.runningClubId,
      };   
      const result: PagedResponseModel<RunningClubEventList> = await getPagedRunningClubEvents(finalReq);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Running Club Events:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/running-clubs/events/new`, {state: {initValue: {runningClubId}, returnTo: runningClubId ? undefined : '/running-clubs/events'}})}>
          Add Event
        </Button>

        <Stack spacing={6}>
          {!runningClubId && <FetchingSelect
            label="Running Club"
            fetchData={(text, page) => getRunningClubsLookup(text, page)}
            value={filterReq.runningClubId}
            onChange={dataKey => setFilterReq(pre => ({...pre, runningClubId: dataKey}))}
          />}           
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({ ...pre, keyword: dataKey }))} 
          />    
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >      
        <Column flexGrow={2} align="left" fullText sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name" />
        </Column>
       {!runningClubId && <Column flexGrow={1} align="left" fullText sortable>
          <HeaderCell>Club</HeaderCell>
          <Cell dataKey="runningClub.name" />
        </Column>}
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Start</HeaderCell>
          <Cell dataKey="startTime">
            {(rowData: RunningClubEventList) => jsDateToZone(rowData.startTime, rowData.ianaTimeZoneId)}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>End</HeaderCell>
          <Cell dataKey="endTime">
            {(rowData: RunningClubEventList) => rowData.endTime ? jsDateToZone(rowData.endTime, rowData.ianaTimeZoneId) : '-'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Visibility</HeaderCell>
          <Cell dataKey="visibility">
            {(rowData: RunningClubEventList) => ClubEventVisibilityList.find(v => v.value === rowData.visibility)?.label ?? 'Unknown'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" fullText sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status">
            {(rowData: RunningClubEventList) => ClubEventStatusList.find(s => s.value === rowData.status)?.label ?? 'Unknown'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center">
          <HeaderCell>Club confirmed</HeaderCell>
          <Cell>
            {(rowData: RunningClubEventList) => rowData.isConfirmedByClub
              ? 'Yes'
              : <Tag color="orange" size="sm">Not confirmed</Tag>}
          </Cell>
        </Column>

        <Column flexGrow={0.5} fixed="right">
          <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {(rowData: RunningClubEventList) => (              
              <IconButton onClick={() => nav(`/running-clubs/events/${rowData.id}`, {state: {initValue: {runningClubId}, returnTo: runningClubId ? undefined : '/running-clubs/events'}})} icon={<EditIcon />} circle size="sm" />
            )}
          </Cell>
        </Column>

      </Table>
      <Divider /> 
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
      
    </PageContent>
  );
};

export default RunningClubEventListPage;
